import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Loader2, CheckCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import { useFeatureFlags } from "@/lib/featureFlags";
import { useAuth } from "@/context/AuthContext";
import { MiniBadge, useBadgePulse } from "@/components/MemberStatBadges";
import { fmtPts } from "@/components/thespian/ThespianRankCard";
import DashboardNotificationsCard from "@/components/notifications/DashboardNotificationsCard";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// One linked student: name + good-standing chip + tiny badges for the features on for that child's team (pulse on change).
function StudentCard({ s, idx, flags, parentId, onOpen, onTab }) {
  const f = s.features || { merit_tracking: flags.merit_tracking, lettering: flags.lettering, thespian: false, rally_books: false };
  const pts = s.letter_status?.total_approved_points || 0;
  const th = s.thespian || {};
  const bal = s.rally_balance || 0;
  const pulse = useBadgePulse(parentId && `${parentId}_${s.id}_list`, {
    merits: f.merit_tracking ? s.net_merit_score ?? 0 : null,
    lettering: f.lettering ? pts : null,
    thespian: f.thespian ? { rank: th.rank || "", pts: th.total_points || 0 } : null,
    rally: f.rally_books ? bal : null,
  });
  const badges = [
    f.merit_tracking && { kind: "merits", label: "Net merits", value: s.net_merit_score ?? 0, tab: "record" },
    f.lettering && { kind: "lettering", label: "Letter pts", value: pts, tab: "record" },
    f.thespian && { kind: "thespian", label: th.rank ? "ITS rank" : "Thespian", value: th.rank || `${fmtPts(th.total_points || 0)} pts`, tab: "record" },
    f.rally_books && { kind: "rally", label: bal > 0 ? "Due" : "Rally Books", value: bal > 0 ? money(bal) : "Paid up", tab: "rally-books" },
  ].filter(Boolean);
  const stacked = badges.length >= 3;
  return (
    <Card onClick={onOpen} style={{ animationDelay: `${idx * 70}ms` }}
      className="group cursor-pointer border-slate-200 p-5 shadow-sm transition-all hover:border-sky-300 hover:shadow-md animate-fade-in-up"
      data-testid={`parent-student-card-${s.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-100 text-sm font-bold text-sky-700">
            {`${(s.first_name || "")[0] || ""}${(s.last_name || "")[0] || ""}`.toUpperCase()}
          </span>
          <div className="leading-tight">
            <p className="font-semibold text-slate-900">{s.first_name} {s.last_name}</p>
            <p className="text-xs text-slate-400">{s.team_name || ""}{s.graduation_year ? ` · Class of ${s.graduation_year}` : ""}</p>
            {s.good_standing !== false && (
              <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700" data-testid={`parent-student-standing-${s.id}`}>
                <CheckCircle2 className="h-3 w-3" /> Good standing
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-sky-500" />
      </div>
      {badges.length > 0 && (
        <div className={`mt-4 grid gap-2 ${GRID[badges.length]}`} data-testid={`parent-student-badges-${s.id}`} data-count={badges.length}>
          {badges.map((b) => (
            <MiniBadge key={b.kind} kind={b.kind} label={b.label} value={b.value} pulse={pulse[b.kind]} stacked={stacked}
              onClick={() => onTab(b.tab)} testId={`parent-student-${b.kind}-${s.id}`} />
          ))}
        </div>
      )}
    </Card>
  );
}
const GRID = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4" };

export default function ParentDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const flags = useFeatureFlags();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/parent/students")
      .then(({ data }) => setStudents(data.items || []))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageHeader title="My Students" subtitle="View-only access to your linked student's merit and lettering record" />
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : students.length === 0 ? (
        <EmptyState title="No linked students yet"
          description="Once your coach approves your account and links your student, they'll appear here."
          testId="parent-no-students" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="parent-students-grid">
          {students.map((s, idx) => <StudentCard key={s.id} s={s} idx={idx} flags={flags} parentId={user?.id}
            onOpen={() => navigate(`/parent/students/${s.id}`)} onTab={(tab) => navigate(`/parent/students/${s.id}/${tab}`)} />)}
        </div>
      )}
      <DashboardNotificationsCard />
    </div>
  );
}
